/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Controller } from 'react-hook-form';
import { FormControl, makeStyles, MenuItem, Select } from '@material-ui/core';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';

const useStyles = makeStyles(theme => ({
  menuItems: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
}));

/**
 * React hook form enabled wrapper selectbox for Material UI select
 * @param control
 * @param name
 * @param label
 * @param id
 * @param defaultValue is expected to be an object not a plain value in form { id, label }
 * @param error
 * @param errorMessage
 * @param validationRules
 * @param items
 * @param itemId
 * @param itemLabel
 * @param variant
 * @param className the className for the formControl component
 * @param disabled
 * @returns Component
 * @constructor
 */
export function CustomSelectBox({
  form,
  control,
  name,
  label,
  id,
  defaultValue,
  error,
  errorMessage,
  validationRules,
  items,
  itemId,
  itemLabel,
  variant = 'outlined',
  className = '',
  disabled,
  disabledItems = [],
}) {
  const classes = useStyles();
  const _control = form ? form.control : control;
  const { errors, clearErrors } = form || {};
  const combinedErrorMessage = errorMessage || errors?.[name]?.message;
  const combinedError = error || !!combinedErrorMessage;

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ onChange, value }) => (
        <FormControl variant={variant} error={combinedError} fullWidth className={className} disabled={disabled}>
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <Select
            id={id}
            labelId={`${name}-label`}
            label={label}
            value={value || ''}
            onChange={(e) => {
              if (errors?.[name]) {
                clearErrors(name);
              }
              onChange(e);
            }}
          >
            {items?.map((item) => {
              return (
                <MenuItem
                  key={item[itemId]}
                  value={item[itemId]}
                  className={classes.menuItems}
                  disabled={disabledItems.includes(item[itemId])}
                >
                  {item[itemLabel]}
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText combinedError>{combinedErrorMessage}</FormHelperText>
        </FormControl>
      )}
      control={_control}
      rules={validationRules}
    />
  );
}
